"use client";

import { useState } from "react";
import { isValidPostcodeFormat, formatPostcode } from "@/lib/postcode";

interface PostcodeInputProps {
  value: string;
  onChange: (value: string) => void;
  name?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  ariaLabel?: string;
  /** Extra classes added to the input (e.g. "bg-cream") */
  extraClassName?: string;
}

export function PostcodeInput({
  value,
  onChange,
  name,
  id,
  placeholder = "e.g. SW1A 1AA",
  required,
  ariaLabel,
  extraClassName,
}: PostcodeInputProps) {
  const [touched, setTouched] = useState(false);

  const getError = (val: string): string => {
    const trimmed = val.trim();
    if (!trimmed) {
      return required ? "Please enter a postcode" : "";
    }
    if (!isValidPostcodeFormat(trimmed)) {
      return "Please enter a valid UK postcode";
    }
    return "";
  };

  const error = touched ? getError(value) : "";
  const borderClass = error ? "border-error" : "border-border";

  return (
    <div>
      <input
        type="text"
        id={id}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          setTouched(true);
          const trimmed = value.trim();
          if (trimmed && isValidPostcodeFormat(trimmed)) {
            onChange(formatPostcode(trimmed));
          }
        }}
        placeholder={placeholder}
        required={required}
        aria-label={ariaLabel}
        autoComplete="postal-code"
        className={`w-full px-4 py-3 border ${borderClass} rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest ${extraClassName || ""}`}
      />
      {error && (
        <p className="text-xs text-error mt-1">{error}</p>
      )}
    </div>
  );
}
